<script setup>
// assets/project/src/views/ToExpert/RvvbProject.vue

import {defineProps, toRefs, defineEmits, ref, computed} from "vue";
import competitionServices from "@/services/competitionServices";
import moment from 'moment';
import DocumentIcon from "@/views/ToExpert/svg/DocumentIcon.vue";
import ProjectVoteComponent from "@/views/ToExpert/ProjectVoteComponent.vue";
import {Tooltip, Collapse, CollapsePanel} from "ant-design-vue"
import {CaretRightOutlined} from '@ant-design/icons-vue';
import DoughnutChartComponent from "@/components/Base/DoughnutChartComponent.vue";

const props = defineProps({
	project: {
		required: true
	},
	projectInfo: {},
	currentUserEmail: {},
	isDataUpdating: {
		type: Boolean,
		default: false
	},
});
const {project, projectInfo} = toRefs(props);

let active_members = ref([]);

let tab = ref(0);

function getTourName(tourNumber) {
	return competitionServices.getTourName(tourNumber);
}

const formattedDate = computed(() => {
	return moment(projectInfo.value.created).format('YYYY-MM-DD HH:mm:ss');
});

const emit = defineEmits(['addToFavorite', 'toggleFavorite'])

function addToFavorite() {
	emit('addToFavorite')
}

function toggleFavorite() {
	emit('toggleFavorite')
}

const downloadFile = (fileUrl) => {
	if (fileUrl) {
		if (fileUrl.startsWith('project')) {
			// Убираем '/project/' из начала строки
			fileUrl = fileUrl.slice(7);
		}
		window.open('https://cdn.moviestart.ru/uploads/pdf/fprk/' + fileUrl, '_blank');
	}
};

const downloadUserDocFile = (fileUrl) => {
	if (fileUrl) {
		if (fileUrl.startsWith('project')) {
			// Убираем '/project/' из начала строки
			fileUrl = fileUrl.slice(7);
		}
		window.open('https://cdn.moviestart.ru/uploads/pdf/user_documents/' + fileUrl, '_blank');
	}
};

const totalCities = computed(() => {
	return project?.value?.stage?.length;
});

const capital = computed(() => {
	return project.value.stage.filter(item =>
		item.gorod === 'Москва' ||
		item.gorod === 'Санкт-Петербург' ||
		item.region === 'Московская область' ||
		item.region === 'Ленинградская область'
	).length;
});


const graphData = computed(() => {
	return {
		datasets: [{
			data: [capitalPercentage.value, regionPercentage.value],
			backgroundColor: [
				'#B32424', // Цвет для Москвы и Санкт-Петербурга
				'#A1E56C' // Цвет для регионов
			],
			borderColor: 'transparent'
		}],
		labels: ['Москва, Санкт-Петербург ' + capitalPercentage.value + '%', 'Регионы ' + regionPercentage.value + '%']
	};
});

const capitalPercentage = computed(() => {
	if (totalCities.value === 0) return 0;
	return ((capital.value * 100) / totalCities.value).toFixed(2);
});

const regionPercentage = computed(() => {
	if (totalCities.value === 0) return 0;
	return (100 - (capital.value * 100 / totalCities.value)).toFixed(2);
});

const getRole = (whois) => {
	switch (whois) {
		case 'regiser':
			return 'Режиссер';
		case 'regiser2':
			return 'Режиссер';
		case 'author_scenario':
			return 'Автор сценария';
		case 'author_scenario2':
			return 'Автор сценария';
		case 'compositor':
			return 'Композитор';
		case 'other':
			return 'Другое';
		default:
			return 'Участник проекта';
	}
};

const getDocumentLink = (documentPath) => {
	return documentPath ? `https://script.moviestart.ru${documentPath}` : '';
};

function getUploadedPdfUserDocAssetUrl(path) {
	return `https://cdn.moviestart.ru/uploads/pdf/user_documents/${path}`;
}

function getUploadedPdfProjectDocAssetUrl(path) {
	return `https://cdn.moviestart.ru/uploads/pdf/fprk/${path}`;
}

const isOrganizator = computed(() => {
	return window.isOrganizatorRole === '1' ? true : false;
})
const isExpert = computed(() => {
	return window.isExpertRole === '1' ? true : false;
})

const truncateText = (text, maxLength) => {
	if (text.length > maxLength) {
		return text.substring(0, maxLength - 3) + '...';
	}
	return text;
};

</script>

<template>
	<div>
		<div class="columns">
			<div class="column">
				<div class="header_title">{{ project.name }}</div>
				<div v-if="projectInfo" class="project_tour_text">{{ getTourName(projectInfo.competition.tour) }}</div>
			</div>
		</div>
		
		<div class="columns">
			<div class="column">
				<div class="columns">
					<div class="column">
						<div class="gray_box">
							<div class="columns" style="display: block">
								<div class="column">
									<p class="countries_block__title">Категория</p>
									<p class="countries_block__sub">
										<template
											v-if="project.project_data && project.project_data.fprk_category === 'choise1'">
											Альманах/полнометражный документальный фильм
										</template>
										<template
											v-else-if="project.project_data && project.project_data.fprk_category === 'choise2'">
											Короткометражный документальный фильм
										</template>
									</p>
								</div>
								<div class="column">
									<p class="countries_block__title">Тема проекта</p>
									<div class="countries_block__sub">
										<ul>
											<li v-for="theme in project.project_data.theme" :key="theme">
												{{ theme }}
											</li>
										</ul>
									</div>
								</div>
							</div>
						</div>
						<div class="gray_box author_box">
							<div class="columns author_columns" v-if="project.user">
								<div class="column author_columns_left">
									<div class="countries_block__title">Автор</div>
									<div class="countries_block__sub"><a :href="'/current-user/'+project.user.id+'/part'"
									                                     target="_blank">{{ project.user.fio }}</a></div>
								</div>
								<div class="column author_columns_right">
									<a target="_blank" :href="'/current-user/'+project.user.id+'/part'" class="button4_white">Подробнее об
										авторе</a>
								</div>
							</div>
						</div>
						<div class="votes__wrapper">
							<ProjectVoteComponent
								v-if="isExpert && projectInfo"
								:project="project"
								:project-info="projectInfo"
								:is-data-updating="isDataUpdating"
								@toggleFavorite="toggleFavorite"
								@add-to-favorite="addToFavorite"
							></ProjectVoteComponent>
						</div>
					</div>
					<div class="column is-4">
						<div class="gray_box command_block">
							<div class="columns">
								<div class="column" style="padding-bottom: 0;">
									<p class="countries_block__title" style="margin-bottom: 10px;">Команда</p>
									<div class="countries_block__sub">
										<div v-if="totalCities > 0" class="columns" style="padding-bottom: 0;">
											<div class="column is-narrow" style="padding-right: 0; padding-bottom: 0;">
												<DoughnutChartComponent
													:data="graphData"
													:index="1"
												></DoughnutChartComponent>
											</div>
											<div class="column" style="display: flex; align-items: center; padding-bottom: 0;">
												<table>
													<tr>
														<td class="title_td">
															<div class="table_circle"
															     style="width: 12px; height: 12px; background: #B32424; border-radius: 20px"></div>
															Москва, Санкт-петербург
														</td>
														<td> {{ capitalPercentage }}%</td>
													</tr>
													<tr>
														<td class="title_td">
															<div class="table_circle"
															     style="width: 12px; height: 12px; background: #A1E56C; border-radius: 20px"></div>
															Регионы
														</td>
														<td>{{ regionPercentage }}%</td>
													</tr>
												</table>
											
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				
				<div class="gray_box base_files">
					<p class="countries_block__title">Основные файлы</p>
					<div class="files_wrapper">
						<Tooltip v-if="project.project_data.prezentation" title="Презентация проекта">
							<div class="file button4_white" @click="downloadFile(project.project_data.prezentation)">
								<DocumentIcon></DocumentIcon>
								<span>{{ truncateText('Презентация проекта', 30) }}</span>
							</div>
						</Tooltip>
						
						<Tooltip v-if="project.project_data.sinopsis" title="Расширенный синопсис проекта">
							<div class="file button4_white" @click="downloadFile(project.project_data.sinopsis)">
								<DocumentIcon></DocumentIcon>
								<span>{{ truncateText('Расширенный синопсис проекта', 30) }}</span>
							</div>
						</Tooltip>
						
						<Tooltip v-if="project.project_data.regiser_ekspl"
						         title="Режиссерская экспликация Проекта, подписанная режиссером">
							<div class="file button4_white" @click="downloadFile(project.project_data.regiser_ekspl)">
								<DocumentIcon></DocumentIcon>
								<span>{{
										truncateText('Режиссерская экспликация Проекта, подписанная режиссером', 30)
									}}</span>
							</div>
						</Tooltip>
						
						<Tooltip v-if="project.project_data.rashodi"
						         title="Лимит затрат по форме, установленной Фондом, на реализацию (создание) Проекта">
							<div class="file button4_white" @click="downloadFile(project.project_data.rashodi)">
								<DocumentIcon></DocumentIcon>
								<span>{{
										truncateText('Лимит затрат по форме, установленной Фондом, на реализацию (создание) Проекта', 30)
									}}</span>
							</div>
						</Tooltip>
						
<!--						<Tooltip v-if="project.project_data.rashodi_2"-->
<!--						         title="Лимит затрат на проект с софинансированием (Приложение № 7.1)">-->
<!--							<div class="file button4_white" @click="downloadFile(project.project_data.rashodi_2)">-->
<!--								<DocumentIcon></DocumentIcon>-->
<!--								<span>{{-->
<!--										truncateText('Лимит затрат на проект с софинансированием (Приложение № 7.1)', 30)-->
<!--									}}</span>-->
<!--							</div>-->
<!--						</Tooltip>-->
						
						<Tooltip v-if="project.project_data.plan_realizatii" title="Календарно-постановочный план">
							<div class="file button4_white" @click="downloadFile(project.project_data.plan_realizatii)">
								<DocumentIcon></DocumentIcon>
								<span>{{ truncateText('Календарно-постановочный план', 30) }}</span>
							</div>
						</Tooltip>
						
						<Tooltip v-if="project.project_data.soglasie"
						         title="Письменное согласие героя (одного из героев) проекта на участие в производстве (создании) Проекта (п. 4.7.17 Документации Конкурсного отбора проектов)">
							<div class="file button4_white" @click="downloadFile(project.project_data.soglasie)">
								<DocumentIcon></DocumentIcon>
								<span>{{
										truncateText('Письменное согласие героя (одного из героев) проекта на участие в производстве (создании) Проекта (п. 4.7.17 Документации Конкурсного отбора проектов)', 30)
									}}</span>
							</div>
						</Tooltip>
						
						<Tooltip v-if="project.project_data.pisma2"
						         title="Письма региональных органов власти и (или) партнеров Проекта, подтверждающих оказание поддержки в реализации (создании) в качественном выражении (п.4.9 Документации Конкурсного отбора проектов). При наличии нескольких писем сформируйте их в один PDF документ.">
							<div class="file button4_white" @click="downloadFile(project.project_data.pisma2)">
								<DocumentIcon></DocumentIcon>
								<span>{{
										truncateText('Письма региональных органов власти и (или) партнеров Проекта, подтверждающих оказание поддержки в реализации (создании) в качественном выражении (п.4.9 Документации Конкурсного отбора проектов). При наличии нескольких писем сформируйте их в один PDF документ.', 30)
									}}</span>
							</div>
						</Tooltip>
						
						
						<Tooltip v-if="project.project_data.additional"
						         title="Дополнительные документы Участника (4.9 Документации Конкурсного отбора проектов)">
							<div class="file button4_white"
							     @click="downloadFile(project.project_data.additional)">
								<DocumentIcon></DocumentIcon>
								<span>{{
										truncateText('Дополнительные документы Участника (4.9 Документации Конкурсного отбора проектов)', 30)
									}}</span>
							</div>
						</Tooltip>
					
					</div>
				</div>
				
				<div class="tabs_wrapper">
					<div class="tab_item" :class="{ 'is_active': tab === 0 }" @click="tab = 0">
						Общая информация
					</div>
					<div class="tab_item" :class="{ 'is_active': tab === 1 }" @click="tab = 1">
						Организация / ИП
					</div>
					<div class="tab_item" :class="{ 'is_active': tab === 2 }" @click="tab = 2">
						Команда
					</div>
				</div>
				
				
				<div class="tab_content" v-show="tab === 0">
					<div class="columns" v-if="projectInfo && projectInfo.created">
						<div class="column left_column_par">
							<span class="par_title">Дата публикации заявки</span>
						</div>
						<div class="column right_column_par">
							<div class="par_text">{{ formattedDate }}</div>
						</div>
					</div>
					
					
					<div class="columns"
					     v-if="project.project_data.predpol_locations && project.project_data.predpol_locations.length > 0">
						<div class="column left_column_par">
							<span class="par_title">Предполагаемые места съемок</span>
						</div>
						<div class="column right_column_par">
							<div v-for="location in project.project_data.predpol_locations" :key="location.id"
							     class="location_wrapper" style="margin-bottom: 25px;">
								<div class="item_content"><strong>Страна: </strong>{{ location.predpol_country || '' }}
								</div>
								<div class="item_content"><strong>Регион: </strong>{{ location.predpol_region || '' }}
								</div>
								<div class="item_content"><strong>Город: </strong>{{ location.predpol_city || '' }}
								</div>
							</div>
						</div>
					</div>
					
					<div class="columns" v-if="project.additional_info">
						<div class="column left_column_par">
							<span class="par_title">Краткий синопсис Проекта</span>
						</div>
						<div class="column right_column_par">
							<div class="item_content" v-html="project.additional_info"></div>
						</div>
					</div>
					
					<div v-if="project.project_data.link_video" class="columns">
						<div class="column left_column_par">
							<span class="par_title">Видеообращение авторов с устной презентацией проекта, представлением команды и опыта команды</span>
						</div>
						<div class="column right_column_par">
							<div class="item_content">
								<a class="right_column_link" target="_blank" :href="project.project_data.link_video">{{ project.project_data.link_video }}</a>
							</div>
						</div>
					</div>
					
					<div v-if="project.project_data.link" class="columns">
						<div class="column left_column_par">
							<span class="par_title">Ссылка на видеоматериалы о проекте, который заявляется на конкурс</span>
						</div>
						<div class="column right_column_par">
							<div class="item_content">
								<a class="right_column_link" target="_blank"
								   :href="project.project_data.link">{{ project.project_data.link }}</a>
							</div>
						</div>
					</div>
					
					<div v-if="project.project_data.social_valibility" class="columns">
						<div class="column left_column_par">
							<span class="par_title">Общественная значимость</span>
						</div>
						<div class="column right_column_par">
							<div class="item_content" v-html="project.project_data.social_valibility"></div>
						</div>
					</div>
					
					<div v-if="project.project_data.aim_auditory" class="columns">
						<div class="column left_column_par">
							<span class="par_title">Целевая аудитория</span>
						</div>
						<div class="column right_column_par">
							<div class="item_content" v-html="project.project_data.aim_auditory"></div>
						</div>
					</div>
					
					<div v-if="project.project_data.plan_promotion" class="columns">
						<div class="column left_column_par">
							<span class="par_title">План фестивального продвижения фильма после завершения реализации Проекта</span>
						</div>
						<div class="column right_column_par">
							<div class="item_content" v-html="project.project_data.plan_promotion"></div>
						</div>
					</div>
					
					<div v-if="project.project_data.canals" class="columns">
						<div class="column left_column_par">
							<span class="par_title">Каналы информационного охвата реализации Проекта</span>
						</div>
						<div class="column right_column_par">
							<div class="item_content" v-html="project.project_data.canals"></div>
						</div>
					</div>
				
				
				</div>
				
				<div class="tab_content" v-show="tab === 1">
					
					<div class="columns">
						<div class="column left_column_par">
							<span class="par_title">Полное наименование организации либо фамилия, имя и отчество (при наличии) Участника - индивидуального предпринимателя</span>
						</div>
						<div class="column right_column_par">
							<div class="item_content" v-html="project.user.user_data.organization_name || ''"></div>
						</div>
					</div>
					
					<div class="columns">
						<div class="column left_column_par">
							<span class="par_title">Сокращенное наименование организации в соответствии с ЕГРЮЛ (для юридических лиц при наличии)</span>
						</div>
						<div class="column right_column_par">
							<div class="item_content"
							     v-html="project.user.user_data.organization_name_short || ''"></div>
						</div>
					</div>
					
					<div class="columns">
						<div class="column left_column_par">
							<span class="par_title">ОГРН/ОГРНИП</span>
						</div>
						<div class="column right_column_par">
							<div class="item_content" v-html="project.user.user_data.ogrn || ''"></div>
						</div>
					</div>
					
					<div class="columns">
						<div class="column left_column_par">
							<span class="par_title">ИНН</span>
						</div>
						<div class="column right_column_par">
							<div class="item_content" v-html="project.user.user_data.inn || ''"></div>
						</div>
					</div>
					
					<div class="columns">
						<div class="column left_column_par">
							<span class="par_title">КПП (для юридических лиц)</span>
						</div>
						<div class="column right_column_par">
							<div class="item_content" v-html="project.user.user_data.kpp || ''"></div>
						</div>
					</div>
					
					<div class="columns">
						<div class="column left_column_par">
							<span class="par_title">Дата регистрации организации/индивидуального предпринимателя</span>
						</div>
						<div class="column right_column_par">
							<div class="item_content" v-html="project.user.user_data.date_register || ''"></div>
						</div>
					</div>
					
					<div class="columns">
						<div class="column left_column_par">
							<span class="par_title">Адрес юридического лица как в ЕГРЮЛ / адрес места жительства как в паспорте (для индивидуального предпринимателя)</span>
						</div>
						<div class="column right_column_par">
							<div class="item_content"><strong>Страна: </strong>{{ project.user.user_data.organization_address_country || '' }}</div>
							<div class="item_content"><strong>Регион: </strong>{{ project.user.user_data.organization_address_region || '' }}</div>
							<div class="item_content"><strong>Город: </strong>{{ project.user.user_data.organization_address_city || '' }}</div>
							<div class="item_content"><strong>Адрес: </strong>{{ project.user.user_data.organization_address_address || '' }}</div>
						</div>
					</div>
					
<!--					<div class="columns">-->
<!--						<div class="column left_column_par">-->
<!--							<span-->
<!--								class="par_title">Адрес (местонахождение)/местожительства (для индивидуального предпринимателя)</span>-->
<!--						</div>-->
<!--						<div class="column right_column_par">-->
<!--							<div class="item_content" v-html="project.user.user_data.organization_address || ''"></div>-->
<!--						</div>-->
<!--					</div>-->
					
					<div class="columns">
						<div class="column left_column_par">
							<span class="par_title">Фактическое местонахождение организации/индивидуального предпринимателя</span>
						</div>
						<div class="column right_column_par">
							<div class="item_content"
							     v-html="project.user.user_data.organization_address_fakt || ''"></div>
						</div>
					</div>
					
					<div class="columns">
						<div class="column left_column_par">
							<span class="par_title">Адрес для направления организации/индивидуальному предпринимателю юридически значимых сообщений</span>
						</div>
						<div class="column right_column_par">
							<div class="item_content"
							     v-html="project.user.user_data.organization_address_notions || ''"></div>
						</div>
					</div>
					
					<div class="columns">
						<div class="column left_column_par">
							<span class="par_title">Фамилия, имя и отчество (при наличии) руководителя организации</span>
						</div>
						<div class="column right_column_par">
							<div class="item_content" v-html="project.user.user_data.fio_boss || ''"></div>
						</div>
					</div>
					
					<div class="columns" v-if="project.user.user_data.polnomovhiya">
						<div class="column left_column_par">
							<span class="par_title">Документы, подтверждающие полномочия лица на осуществление действий от имени Участника юридического лица:
1.1. решение о назначении или избрании физического лица на должность: с подтверждением нотариального удостоверения факта принятия такого решения в обществах с ограниченной ответственностью - если решение датировано после 01.09.2024/до 01.09.2024 и при этом Уставом не предусмотрен иной способ удостоверения. в иных случаях, в том числе для юридических лиц других организационно-правовых форм, скан копия должна быть удостоверена уполномоченным лицом Участника.
1.2. скан копия приказа о вступлении физического лица в должность единоличного исполнительного органа (генерального директор/директора и т.п.), в соответствии с которым такое физическое лицо обладает правом действовать от имени заявителя без доверенности).</span>
						</div>
						<div class="column right_column_par">
							<div class="file button4_white"
							     @click="downloadUserDocFile(project.user.user_data.polnomovhiya)">
								<DocumentIcon></DocumentIcon>
								<span>Открыть файл</span>
							</div>
							
							
							<!--                            <div class="item_content">-->
							<!--                                <a :href="getUploadedPdfUserDocAssetUrl(project.user.user_data.polnomovhiya)"-->
							<!--                                   target="_blank">Ссылка на скачивание документа</a>-->
							<!--                            </div>-->
							<!--                            <div class="item_content">-->
							<!--                                <a :href="'https://docs.google.com/gview?url=' + encodeURIComponent(getUploadedPdfUserDocAssetUrl(project.user.user_data.polnomovhiya))"-->
							<!--                                   target="_blank">Просмотр в браузере</a>-->
							<!--                            </div>-->
						</div>
					</div>
					
					
					<div class="columns">
						<div class="column left_column_par">
							<span class="par_title">У организации-заявителя есть лица, имеющие право подписи без доверенности, кроме руководителя.</span>
						</div>
						<div class="column right_column_par">
							<div class="item_content">{{ project.user.user_data.pravo_without ? 'Да' : 'Нет' }}</div>
						</div>
					</div>
					
					<div class="columns">
						<div class="column left_column_par">
							<span class="par_title">Сведения о лице, уполномоченном действовать от имени Участника по доверенности (если применимо)</span>
						</div>
						<div class="column right_column_par">
							<div class="item_content" v-html="project.user.user_data.predstaviteli || ''"></div>
						</div>
					</div>
					
					<div class="columns">
						<div class="column left_column_par">
							<span class="par_title">Доверенность лица, уполномоченного действовать от имени Участника по доверенности (если применимо)</span>
						</div>
						<div class="column right_column_par" v-if="project.user.user_data.doverennost">
							<div class="item_content" @click="downloadUserDocFile(project.user.user_data.doverennost)">
								<DocumentIcon></DocumentIcon>
								<span>Ссылка на скачивание документа</span>
							</div>
						</div>
					</div>
					
					<div class="columns">
						<div class="column left_column_par">
							<span class="par_title">Адрес электронной почты организации/индивидуального предпринимателя</span>
						</div>
						<div class="column right_column_par">
							<div class="item_content">{{ project.user.user_data.organization_email || '' }}</div>
						</div>
					</div>
					
					<div class="columns">
						<div class="column left_column_par">
							<span class="par_title">Контактный телефон</span>
						</div>
						<div class="column right_column_par">
							<div class="item_content">{{ project.user.user_data.organization_phone || '' }}</div>
						</div>
					</div>
					
					<div class="columns">
						<div class="column left_column_par">
							<span class="par_title">Система налогообложения (ОСНО, УСН 15%, УСН 6%)</span>
						</div>
						<div class="column right_column_par">
							<div class="item_content">{{ project.user.user_data.organization_nalogi || '' }}</div>
						</div>
					</div>
					
					<div class="columns">
						<div class="column left_column_par">
							<span class="par_title">Банковские реквизиты</span>
						</div>
						<div class="column right_column_par">
							<div class="item_content">{{ project.user.user_data.organization_rekviziti || '' }}</div>
						</div>
					</div>
					
<!--					<div class="columns">-->
<!--						<div class="column left_column_par">-->
<!--							<span class="par_title">Наличие удостоверения национального фильма/планируется ли получение удостоверения национального фильма</span>-->
<!--						</div>-->
<!--						<div class="column right_column_par">-->
<!--							<div class="item_content">{{ project.user.user_data.yd_nats || '' }}</div>-->
<!--						</div>-->
<!--					</div>-->
					
					<div class="columns" v-if="project.user.user_data.ystav_copy">
						<div class="column left_column_par">
							<span class="par_title">Копия устава</span>
						</div>
						<div class="column right_column_par">
							<div class="file button4_white"
							     @click="downloadUserDocFile(project.user.user_data.ystav_copy)">
								<DocumentIcon></DocumentIcon>
								<span>Открыть файл</span>
							</div>
							
							
							<!--                            <div class="item_content">-->
							<!--                                <a :href="getUploadedPdfUserDocAssetUrl(project.user.user_data.ystav_copy)"-->
							<!--                                   target="_blank">Ссылка на скачивание документа</a>-->
							<!--                            </div>-->
							<!--                            <div class="item_content">-->
							<!--                                <a :href="'https://docs.google.com/gview?url=' + encodeURIComponent(getUploadedPdfUserDocAssetUrl(project.user.user_data.ystav_copy))"-->
							<!--                                   target="_blank">Просмотр в браузере</a>-->
							<!--                            </div>-->
						</div>
					</div>
					
					<div class="columns" v-if="project.user.user_data.svidetelstvo">
						<div class="column left_column_par">
							<span class="par_title">Свидетельство о государственной регистрации или Лист записи ЕГРЮЛ/ЕГРИП</span>
						</div>
						<div class="column right_column_par">
							<div class="file button4_white"
							     @click="downloadUserDocFile(project.user.user_data.svidetelstvo)">
								<DocumentIcon></DocumentIcon>
								<span>Открыть файл</span>
							</div>
							
							
							<!--                            <div class="item_content">-->
							<!--                                <a :href="getUploadedPdfUserDocAssetUrl(project.user.user_data.svidetelstvo)"-->
							<!--                                   target="_blank">Ссылка на скачивание документа</a>-->
							<!--                            </div>-->
							<!--                            <div class="item_content">-->
							<!--                                <a :href="'https://docs.google.com/gview?url=' + encodeURIComponent(getUploadedPdfUserDocAssetUrl(project.user.user_data.svidetelstvo))"-->
							<!--                                   target="_blank">Просмотр в браузере</a>-->
							<!--                            </div>-->
						</div>
					</div>
					
					<div class="columns" v-if="project.user.user_data.vipiska">
						<div class="column left_column_par">
							<span class="par_title">Выписка в электронном виде из единого государственного реестра юридических лиц или из единого государственного реестра индивидуальных предпринимателей, выданная не позднее чем за один месяц до подачи Заявки</span>
						</div>
						<div class="column right_column_par">
							<div class="file button4_white"
							     @click="downloadUserDocFile(project.user.user_data.vipiska)">
								<DocumentIcon></DocumentIcon>
								<span>Открыть файл</span>
							</div>
							
							<!--                            <div class="item_content">-->
							<!--                                <a :href="getUploadedPdfUserDocAssetUrl(project.user.user_data.vipiska)"-->
							<!--                                   target="_blank">Ссылка на скачивание документа</a>-->
							<!--                            </div>-->
							<!--                            <div class="item_content">-->
							<!--                                <a :href="'https://docs.google.com/gview?url=' + encodeURIComponent(getUploadedPdfUserDocAssetUrl(project.user.user_data.vipiska))"-->
							<!--                                   target="_blank">Просмотр в браузере</a>-->
							<!--                            </div>-->
						</div>
					</div>
					
					
					<div class="columns" v-if="project.user.user_data.copy_pasport">
						<div class="column left_column_par">
							<span class="par_title">Копия паспорта (для индивидуальных предпринимателей)</span>
						</div>
						<div class="column">
							<div class="file button4_white"
							     @click="downloadUserDocFile(project.user.user_data.copy_pasport)">
								<DocumentIcon></DocumentIcon>
								<span>Открыть файл</span>
							</div>
							
							
							<!--                            <p class="item_content">-->
							<!--                                <a :href="getUploadedPdfUserDocAssetUrl(project.user.user_data.copy_pasport)"-->
							<!--                                   target="_blank">Ссылка на скачивание документа</a>-->
							<!--                            </p>-->
							<!--                            <p class="item_content">-->
							<!--                                <a :href="'https://docs.google.com/gview?url=' + encodeURIComponent(getUploadedPdfUserDocAssetUrl(project.user.user_data.copy_pasport))"-->
							<!--                                   target="_blank">Просмотр в браузере</a>-->
							<!--                            </p>-->
						</div>
					</div>
					
					<div class="columns" v-if="project.user.user_data.spravka">
						<div class="column left_column_par">
							<span class="par_title">Справка об исполнении налогоплательщиком обязанности по уплате налогов, сборов, страховых взносов, пеней, штрафов, процентов по форме по КНД 1120101</span>
						</div>
						<div class="column right_column_par">
							<div class="file button4_white"
							     @click="downloadUserDocFile(project.user.user_data.spravka)">
								<DocumentIcon></DocumentIcon>
								<span>Открыть файл</span>
							</div>
							
							<!--                            <div class="item_content">-->
							<!--                                <a :href="getUploadedPdfUserDocAssetUrl(project.user.user_data.spravka)"-->
							<!--                                   target="_blank">Ссылка на скачивание документа</a>-->
							<!--                            </div>-->
							<!--                            <div class="item_content">-->
							<!--                                <a :href="'https://docs.google.com/gview?url=' + encodeURIComponent(getUploadedPdfUserDocAssetUrl(project.user.user_data.spravka))"-->
							<!--                                   target="_blank">Просмотр в браузере</a>-->
							<!--                            </div>-->
						</div>
					</div>
					
					<div class="columns" v-if="project.user.user_data.big_deal">
						<div class="column left_column_par">
							<span class="par_title">Документ об одобрении крупной сделки – заключения договора создания документального фильма в случае победы в Конкурсном отборе проектов, если сделка является для заявителя крупной, либо декларация о том, что сделка не является крупной для заявителя и не подлежит одобрению (документы по данному пункту прикрепляются только Участниками в форме ООО, АО, АНО, если законом для них предусмотрено такое одобрение).</span>
						</div>
						<div class="column right_column_par">
							<div class="file button4_white"
							     @click="downloadUserDocFile(project.user.user_data.big_deal)">
								<DocumentIcon></DocumentIcon>
								<span>Открыть файл</span>
							</div>
							
							<!--                            <div class="item_content">-->
							<!--                                <a :href="getUploadedPdfUserDocAssetUrl(project.user.user_data.big_deal)"-->
							<!--                                   target="_blank">Ссылка на скачивание документа</a>-->
							<!--                            </div>-->
							<!--                            <div class="item_content">-->
							<!--                                <a :href="'https://docs.google.com/gview?url=' + encodeURIComponent(getUploadedPdfUserDocAssetUrl(project.user.user_data.big_deal))"-->
							<!--                                   target="_blank">Просмотр в браузере</a>-->
							<!--                            </div>-->
						</div>
					</div>
					
					<div class="columns" v-if="project.user.user_data.deal_win">
						<div class="column left_column_par">
							<span class="par_title">Документ о том, что сделка по заключению с Фондом договора создания документального фильма в случае победы в Конкурсном отборе проектов, не является для заявителя сделкой с заинтересованностью</span>
						</div>
						<div class="column right_column_par">
							<div class="file button4_white"
							     @click="downloadUserDocFile(project.user.user_data.deal_win)">
								<DocumentIcon></DocumentIcon>
								<span>Открыть файл</span>
							</div>
							
							
							<!--                            <div class="item_content">-->
							<!--                                <a :href="getUploadedPdfUserDocAssetUrl(project.user.user_data.deal_win)"-->
							<!--                                   target="_blank">Ссылка на скачивание документа</a>-->
							<!--                            </div>-->
							<!--                            <div class="item_content">-->
							<!--                                <a :href="'https://docs.google.com/gview?url=' + encodeURIComponent(getUploadedPdfUserDocAssetUrl(project.user.user_data.deal_win))"-->
							<!--                                   target="_blank">Просмотр в браузере</a>-->
							<!--                            </div>-->
						</div>
					</div>
					
					<div class="columns" v-if="project.project_data.zayavlenie">
						<div class="column left_column_par">
							<span class="par_title">Подтверждение подачи Заявки на участие в Конкурсном отборе проектов (п. 4.7.19 Документации Конкурсного отбора проектов)</span>
						</div>
						<div class="column right_column_par">
							<div class="file button4_white" @click="downloadFile(project.project_data.zayavlenie)">
								<DocumentIcon></DocumentIcon>
								<span>Открыть файл</span>
							</div>
						</div>
					</div>
					
<!--					<div class="columns" v-if="project.project_data.zayavlenie">-->
<!--						<div class="column left_column_par">-->
<!--							<span class="par_title">Подтверждение подачи Заявки на участие в Конкурсном отборе проектов</span>-->
<!--						</div>-->
<!--						<div class="column right_column_par">-->
<!--							<div class="file button4_white"-->
<!--							     @click="downloadFile(project.project_data.zayavlenie)">-->
<!--								<DocumentIcon></DocumentIcon>-->
<!--								<span>Открыть файл</span>-->
<!--							</div>-->
<!--						</div>-->
<!--					</div>-->
					
					<div class="columns">
						<div class="column left_column_par">
							<span class="par_title">Опыт организации</span>
						</div>
						<div class="column right_column_par">
							<div class="item_content">{{ project.user.user_data.organization_opit || '' }}</div>
						</div>
					</div>
					
					<div class="columns">
						<div class="column left_column_par">
							<span class="par_title">Опыт руководителя</span>
						</div>
						<div class="column right_column_par">
							<div class="item_content">{{ project.user.user_data.bos_opit || '' }}</div>
						</div>
					</div>
				</div>
				
				<div class="tab_content" v-show="tab === 2">
					
					<div v-for="(item, index) in project.stage" :key="index" class="opit_block">
						<Collapse v-model:activeKey="active_members"
						          :bordered="false">
							<template #expandIcon="{ isActive }">
								<caret-right-outlined :rotate="isActive ? 90 : 0"/>
							</template>
							<CollapsePanel :key="index">
								<template #header>
									<div class="columns">
										<div class="column">
											{{ item.last_name || '' }} {{ item.first_name || '' }}
											{{ item.patronymic || '' }}
											({{ item.whois === 'other' ? item.role : getRole(item.whois) }})
										</div>
									</div>
								</template>
								<div v-if="!item.other_country">
									<div class="columns">
										<div class="column is-4"><p class="item_title">Город</p></div>
										<div class="column">{{ item.gorod || '' }}</div>
									</div>
									<div class="columns">
										<div class="column is-4"><p class="item_title">Регион</p></div>
										<div class="column">{{ item.region || '' }}</div>
									</div>
								</div>
								
								<div v-else>
									<div class="columns">
										<div class="column is-4"><p class="item_title">Страна</p></div>
										<div class="column">{{ item.country || '' }}</div>
									</div>
								</div>
								<div v-if="['regiser', 'regiser2', 'author_scenario', 'compositor', 'author_scenario2'].includes(item.whois)">
									<div class="columns">
										<div class="column is-4"><p class="item_title">Сканированная копия договора </p>
										</div>
										<div class="column">
											<p class="item_content" v-if="project.project_data['dogovor' + (index)]">
												<a target="_blank"
												   :href="getUploadedPdfProjectDocAssetUrl(project.project_data['dogovor' + (index)])">Ссылка
													на
													скачивание документа</a>
											</p>
											<p class="item_content" v-if="project.project_data['dogovor' + (index)]">
												<a target="_blank"
												   :href="`https://docs.google.com/gview?url=${getUploadedPdfProjectDocAssetUrl(project.project_data['dogovor' + (index)])}`">Просмотр
													в браузере</a>
											</p>
										</div>
									</div>
								</div>
								
								<div class="columns">
									<div class="column is-4"><p class="item_title">Опыт</p></div>
									<div class="column opit_items">
										<div v-for="opitItem in item.opit || []" :key="opitItem.id" class="opit_item">
											<p><strong>Название проекта:</strong> {{ opitItem.project_name || '' }}</p>
											<p><strong>Год реализации:</strong> {{ opitItem.year_realezatii || '' }}</p>
											<p><strong>Роль на проекте:</strong> {{ opitItem.role || '' }}</p>
											<p><strong>Ссылка на информацию о проекте: </strong>
												<!--																				-->
												<a style="word-break: break-all" target="_blank"
												   :href="opitItem.link || ''">{{ opitItem.link || '' }}</a>
											</p>
											<div><strong>Сведения об участии ранее созданных проектов в российских и
												международных фестивалях:</strong>
												<div class="svedenia" style="word-break: break-all">
													{{ opitItem.description || '' }}
												</div>
											</div>
										</div>
									</div>
								</div>
								
								<div v-if="item.addinfo" class="columns">
									<div class="column is-4"><p class="item_title">Дополнительная информация</p></div>
									<div class="column" v-html="item.addinfo || ''"></div>
								</div>
							</CollapsePanel>
						</Collapse>
					</div>
				</div>
			
			
			</div>
		</div>
	</div>
</template>

<style lang="scss" scoped>
.votes__wrapper:deep .scores_box {
	margin-bottom: 30px;
}

.request_content:deep .opit_item {
	margin-left: 0;
}

.opit_block {
	margin-left: 0 !important;
	padding: 0 !important;
	border-radius: 12px;
}

.opit_block:deep .ant-collapse-header {
	padding-bottom: 0;
}

.item_title {
	margin-bottom: 0;
}

.command_block table {
	font-weight: 400;
	font-size: 14px;
	color: #1d232d;
}

.command_block table .title_td {
	display: flex;
	align-items: center;
}

.command_block table .title_td .table_circle {
	margin-right: 10px;
	flex-basis: 15px;
	max-width: 12px;
}

.item_content svg {
	margin-right: 15px;
}

.right_column_link {
	word-break: break-all;
}
</style>